import React from 'react';

import { Box } from '@mui/material';
import { Product } from '../../common/components/Product';
import {
  FlexCol,
  FlexRow,
  Spacer,
  TitleSmall,
} from '../../common/components/v2';
import { Resource } from '../../common/constants/resource';
import { getFormattedTimestamp } from '../../common/helpers';
import { getAge } from '../detail/utilities/formattingUtilities';
import { PolicyData } from './dtos/policyData.dto';
import styles from './styles.module.scss';

const OverviewRow = ({ label, info }: { label: string; info: string }) => {
  return (
    <>
      <Spacer height={16} />
      <FlexRow>
        <FlexCol sm={3} lg={3} className={styles.Label}>
          {label}
        </FlexCol>
        <FlexCol sm={9} lg={9} className={styles.Info}>
          {info}
        </FlexCol>
      </FlexRow>
    </>
  );
};

const ProductRow = ({
  label,
  product,
  carrier,
  subCategory,
  bundledPolicy,
}: {
  label: string;
  product: string;
  carrier: string;
  subCategory: string;
  bundledPolicy?: any;
}) => {
  return (
    <>
      <Spacer height={16} />
      <FlexRow>
        <FlexCol sm={3} lg={3} className={styles.Label}>
          {label}
        </FlexCol>
        <FlexCol sm={9} lg={9} className={styles.Info}>
          <Box sx={{ width: '100%' }}>
            <Product
              carrier={carrier}
              product={product}
              subCategory={subCategory}
            ></Product>
            {bundledPolicy && !bundledPolicy.isStacked && (
              <span>{` (${bundledPolicy.productId} - bundled purchase)`}</span>
            )}
          </Box>
        </FlexCol>
      </FlexRow>
    </>
  );
};

export const Overview = ({ policy }: { policy: PolicyData }) => {
  const {
    creationDate,
    carrier,
    product,
    subCategory,
    policyId,
    firstName,
    middleName,
    lastName,
    age,
    nearestAge,
    gender,
    height,
    weight,
    income,
    birthState,
    birthCountry,
    stateAtSubmission,
    address,
  } = policy;

  let birthLocation;
  if (birthState) {
    birthLocation = `${birthState}, ${birthCountry}`;
  } else {
    birthLocation = birthCountry;
  }
  const name = `${firstName}${middleName ? ' ' + middleName : ''} ${lastName}`;

  const incomeDisplay = income
    ? `$${income.toLocaleString()}`
    : Resource.NORA_M_DASH;
  const heightDisplay =
    Number.isFinite(Number(height)) && height !== null
      ? getFormatedHeight(height)
      : Resource.NORA_M_DASH;
  const weightDisplay =
    Number.isFinite(Number(weight)) && weight !== null
      ? `${weight}lbs`
      : Resource.NORA_M_DASH;

  return (
    <div data-label={'overview_section'}>
      <FlexRow className={styles.Title}>
        <TitleSmall>{Resource.TASK_TITLE_OVERVIEW}</TitleSmall>
      </FlexRow>
      <OverviewRow
        label={'Date Created'}
        info={getFormattedTimestamp(creationDate)}
      />
      <ProductRow
        label={'Product'}
        carrier={carrier}
        product={product}
        subCategory={subCategory}
        bundledPolicy={policy.bundledPolicyDetails}
      />
      <OverviewRow label={'Carrier'} info={carrier || Resource.NORA_M_DASH} />
      <OverviewRow label={'PolicyID'} info={policyId.toString(10)} />
      <OverviewRow label={'Name'} info={name} />
      {getGenderAndAge(gender, age, nearestAge)}
      <OverviewRow
        label={'Birthplace'}
        info={birthLocation || Resource.NORA_M_DASH}
      />
      <OverviewRow label={'Address'} info={address || Resource.NORA_M_DASH} />
      <OverviewRow
        label={'State at Submission'}
        info={stateAtSubmission || Resource.NORA_M_DASH}
      />
      {Number.isFinite(Number(income)) && (
        <OverviewRow label={'Income'} info={incomeDisplay} />
      )}
      <OverviewRow
        label={'Height/Weight'}
        info={`${heightDisplay} / ${weightDisplay}`}
      />
    </div>
  );
};

const getFormatedHeight = (height: number) => {
  const htFeet = Math.floor(height / 12);
  const htInch = height % 12;
  if (htFeet) {
    return `${htFeet}ft ${htInch}in`;
  } else {
    // For backward compatibility . we will remove this once we fix data in BE
    return `${Math.floor(height)}ft ${Math.round(
      (Number(height) * 12) % 12,
    )}in`;
  }
};

const getGenderAndAge = (gender: string, age: number, nearestAge: number) => {
  const ageDisplay = getAge(age, nearestAge);

  return (
    <OverviewRow label={'Gender & Age'} info={`${gender}, ${ageDisplay}`} />
  );
};
